/* eslint-disable no-console */

/*
  parseLocalStorage -> filter incorrect data -> mix input with localStorage data -> save to localStorage
*/
import type {
  AdditionalInterestAndInsured,
  Address,
  BuildingAddress,
} from '../../configs/APISchemas/Application.schema';
import type { PurchasePolicyOptionalCoverages } from '../../redux/reducer/v2/purchasePolicy';
import type { Unit } from '../../types/Store.types';
import type {
  Keys,
  PickedPurchasePolicyAdditionalData,
  PickedPurchasePolicyFlow,
  PickedPurchasePolicyInput,
  PickedShared,
  PickedSystemSettings,
  PickedVerifyPolicyComplianceProvider,
  PickedVerifyPolicyFlow,
  PickedVerifyPolicyInput,
  PurchasePolicyLocalStorage,
  SharedLocalStorage,
  SystemSettingsLocalStorage,
  VerifyPolicyLocalStorage,
} from './validateLocalStorage.types';
import {
  addressValidate,
  validateAdditionalInsuredRoommates,
  validateAdditionalInterests,
  validateBoolean,
  validateCurrentStep,
  validateErrors,
  validateNumber,
  validateOptionalCoverages,
  validateString,
  validateUnit,
} from './validateLocalStorage.validator';

export enum localStorageKey {
  purchasePolicyInput = 'purchasePolicyInput',
  purchasePolicyAdditionalData = 'purchasePolicyAdditionalData',
  purchasePolicyFlow = 'purchasePolicyFlow',
  verifyPolicyInput = 'verifyPolicyInput',
  verifyPolicyFlow = 'verifyPolicyFlow',
  verifyPolicyComplianceProvider = 'verifyPolicyComplianceProvider',
  verifyPolicyAdditionalData = 'verifyPolicyAdditionalData',
  systemSettings = 'systemSettings',
  shared = 'shared',
  // Techincal values, used to track the flow of the application
  verifyPolicy = 'verifyPolicy',
  purchasePolicy = 'purchasePolicy',
}

const validationMapByKeys: {
  [key in Keys[number]]: (value: any) => boolean;
} = {
  firstName: validateString,
  lastName: validateString,
  email: validateString,
  phone: validateString,
  unit: validateUnit,
  addressName: validateString,
  address: addressValidate,
  buildingUnit: validateString,
  buildingId: validateString,
  buildingName: validateString,
  buildingAddress: addressValidate,
  coverageStart: validateString,
  coverageEnd: validateString,
  leaseStart: validateString,
  leaseEnd: validateString,
  personalPropertyCoverage: validateNumber,
  personalLiabilityCoverage: validateNumber,
  medicalPayments: validateNumber,
  deductible: validateNumber,
  heading: validateNumber,
  waterBackup: validateNumber,
  petDamage: validateNumber,
  earthquake: validateNumber,
  hurricane: validateNumber,
  lossOfUse: validateNumber,
  theft: validateNumber,
  windHail: validateNumber,
  identityFraud: validateNumber,
  additionalInterests: validateAdditionalInterests,
  additionalInsuredRoommates: validateAdditionalInsuredRoommates,
  optionalCoverages: validateOptionalCoverages,

  id: validateString,
  communityId: validateString,
  underwritingCompanyId: validateString,

  isClickedStartApplicationContinue: validateBoolean,
  isClickedQuoteInformationContinue: validateBoolean,
  hasVisitedCoverageDetails: validateBoolean,
  hasClickedComplianceContinue: validateBoolean,
  userFinishedComplianceFlow: validateBoolean,

  utmSource: validateString,
  currentRoute: validateString,
  currentStep: validateCurrentStep,

  activeCovieLink: validateString,
  errors: validateErrors,

  companyVendor: validateString,
};

const purchasePolicyInputKeys: (keyof PickedPurchasePolicyInput)[] = [
  'firstName',
  'lastName',
  'email',
  'phone',
  'unit',
  'addressName',
  'address',
  'buildingUnit',
  'buildingId',
  'buildingName',
  'buildingAddress',
  'coverageStart',
  'coverageEnd',
  'personalPropertyCoverage',
  'personalLiabilityCoverage',
  'medicalPayments',
  'deductible',
  'heading',
  'waterBackup',
  'petDamage',
  'earthquake',
  'hurricane',
  'lossOfUse',
  'theft',
  'windHail',
  'identityFraud',
  'additionalInterests',
  'additionalInsuredRoommates',
  'optionalCoverages',
];

const purchasePolicyAdditionalDataKeys: (keyof PickedPurchasePolicyAdditionalData)[] =
  ['id', 'communityId', 'underwritingCompanyId'];

const purchasePolicyFlowKeys: (keyof PickedPurchasePolicyFlow)[] = [
  'isClickedStartApplicationContinue',
  'isClickedQuoteInformationContinue',
  'hasVisitedCoverageDetails',
];

const transformRawData = <K extends Keys>(keys: K, data: string | null) => {
  if (!data) return null;
  try {
    const parsedInputData = JSON.parse(data);
    return transformParsedData(keys, parsedInputData);
  } catch (e) {
    console.error('Error parsing data from localStorage');
  }
};

const transformOptionalCoverages = (data: {
  [key in keyof PurchasePolicyOptionalCoverages]: unknown;
}): PurchasePolicyOptionalCoverages => {
  return {
    fireDepartmentService: validateBoolean(data.fireDepartmentService)
      ? Boolean(data.fireDepartmentService)
      : false,
    sinkHole: validateBoolean(data.sinkHole) ? Boolean(data.sinkHole) : false,
    ordinanceOrLaw: validateBoolean(data.ordinanceOrLaw)
      ? Boolean(data.ordinanceOrLaw)
      : false,
    rentalIncome: validateBoolean(data.rentalIncome)
      ? Boolean(data.rentalIncome)
      : false,
    workersCompensation: validateBoolean(data.workersCompensation)
      ? Boolean(data.workersCompensation)
      : false,
  };
};

const transformAdditionalInterests = (data: AdditionalInterestAndInsured[]) => {
  return data.map((item) => {
    return {
      firstName: validateString(item.firstName) ? item.firstName : '',
      lastName: validateString(item.lastName) ? item.lastName : '',
      email: validateString(item.email) ? item.email : '',
    };
  });
};

const transformErrors = (
  data: {
    id: string;
    message: string;
    type: string;
  }[],
) => {
  return data.map((item) => ({
    id: validateString(item.id) ? item.id : '',
    message: validateString(item.message) ? item.message : '',
    type: validateString(item.type) ? item.type : '',
  }));
};

const transformBuildingAddress = (data: BuildingAddress): BuildingAddress => {
  return {
    value: {
      state: validateString(data.value.state) ? data.value.state : '',
      postalCode: validateString(data.value.postalCode)
        ? data.value.postalCode
        : '',
      municipality: validateString(data.value.municipality)
        ? data.value.municipality
        : '',
      street: validateString(data.value.street) ? data.value.street : '',
      addressNumber: validateString(data.value.addressNumber)
        ? data.value.addressNumber
        : '',
    },
  };
};

const transformAddress = (data: Address): Address => {
  return {
    state: validateString(data.state) ? data.state : '',
    postalCode: validateString(data.postalCode) ? data.postalCode : '',
    municipality: validateString(data.municipality) ? data.municipality : '',
    street: validateString(data.street) ? data.street : '',
    addressNumber: validateString(data.addressNumber) ? data.addressNumber : '',
    shortNameStreet: validateString(data.shortNameStreet)
      ? data.shortNameStreet
      : '',
  };
};

const transformUnit = (data: Unit): Unit => {
  return {
    id: validateString(data.id) ? data.id : '',
    name: validateString(data.name) ? data.name : '',
  };
};

const transformParsedData = <K extends Keys>(keys: K, data: any) => {
  return keys.reduce((acc, key) => {
    if (key in data && validationMapByKeys[key](data[key])) {
      if (key === 'unit') {
        acc[key] = transformUnit(data[key]);
      } else if (key === 'address') {
        acc[key] = transformAddress(data[key]);
      } else if (key === 'optionalCoverages') {
        acc[key] = transformOptionalCoverages(data[key]);
      } else if (key === 'additionalInterests') {
        acc[key] = transformAdditionalInterests(data[key]);
      } else if (key === 'additionalInsuredRoommates') {
        acc[key] = transformAdditionalInterests(data[key]);
      } else if (key === 'buildingAddress') {
        acc[key] = transformBuildingAddress(data[key]);
      } else if (key === 'errors') {
        acc[key] = transformErrors(data[key]);
      } else {
        acc[key] = data[key];
      }
    }
    return acc;
    // TODO: fix type
  }, {} as any);
};

const verifyPolicyInputKeys: (keyof PickedVerifyPolicyInput)[] = [
  'firstName',
  'lastName',
  'leaseEnd',
  'leaseStart',
  'address',
  'unit',
  'addressName',
  'email',
  'buildingUnit',
  'buildingId',
  'buildingName',
];

const verifyPolicyFlowKeys: (keyof PickedVerifyPolicyFlow)[] = [
  'hasClickedComplianceContinue',
  'userFinishedComplianceFlow',
];

const verifyPolicyComplianceProviderKeys: (keyof PickedVerifyPolicyComplianceProvider)[] =
  ['activeCovieLink', 'errors'];

const systemSettingsKeys: (keyof PickedSystemSettings)[] = [
  'utmSource',
  'currentRoute',
  'currentStep',
];
const sharedKeys: (keyof PickedShared)[] = ['companyVendor'];

export const validatePurchasePolicyLocalStorage =
  (): PurchasePolicyLocalStorage => {
    const rawInputData = localStorage.getItem(localStorageKey.purchasePolicyInput);
    const rawAdditionalData = localStorage.getItem(localStorageKey.purchasePolicyAdditionalData);
    const rawFlowData = localStorage.getItem(localStorageKey.purchasePolicyFlow);

    return {
      input: transformRawData(purchasePolicyInputKeys, rawInputData),
      additionalData: transformRawData(
        purchasePolicyAdditionalDataKeys,
        rawAdditionalData,
      ),
      flow: transformRawData(purchasePolicyFlowKeys, rawFlowData),
      complianceProvider: null,
    };
  };

export const validateVerifyPolicyLocalStorage =
  (): VerifyPolicyLocalStorage => {
    const rawInputData = localStorage.getItem(
      localStorageKey.verifyPolicyInput,
    );
    const rawFlowData = localStorage.getItem(localStorageKey.verifyPolicyFlow);
    const rawComplianceProviderData = localStorage.getItem(
      localStorageKey.verifyPolicyComplianceProvider,
    );

    return {
      input: transformRawData(verifyPolicyInputKeys, rawInputData),
      flow: transformRawData(verifyPolicyFlowKeys, rawFlowData),
      complianceProvider: transformRawData(
        verifyPolicyComplianceProviderKeys,
        rawComplianceProviderData,
      ),
      additionalData: null,
    };
  };

export const validateSystemSettingsLocalStorage =
  (): SystemSettingsLocalStorage => {
    const rawInputData = localStorage.getItem(localStorageKey.systemSettings);

    return transformRawData(systemSettingsKeys, rawInputData);
  };

export const validateSharedLocalStorage = (): SharedLocalStorage => {
  const rawInputData = localStorage.getItem(localStorageKey.shared);

  return transformRawData(sharedKeys, rawInputData);
};
